import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { ButtonRounded } from './Buttons/ButtonRounded';
import PillInput from './Forms/PillInput';
import { SHADOWS } from '../../styles/UISystem';

/**
 * GroupSelector Component
 *
 * Bottom sheet overlay for assigning groups to a connection.
 * DB-first: reads from hook state, writes via hook actions.
 *
 * Props:
 * - groups: Array of all user's group objects (presets + custom)
 * - selectedGroupIds: string[] of currently selected group UUIDs
 * - onSave: (groupIds: string[]) => void - callback with final selection
 * - onClose: () => void
 * - onCreateGroup: (name: string) => Promise<Object|null> - creates custom group
 * - onDeleteGroup: (groupId: string) => Promise<boolean> - deletes custom group
 */
const GroupSelector = ({
  groups = [],
  selectedGroupIds = [],
  onSave,
  onClose,
  onCreateGroup,
  onDeleteGroup,
}) => {
  const [localSelected, setLocalSelected] = useState(new Set(selectedGroupIds));
  const [customGroupInput, setCustomGroupInput] = useState('');
  const [localGroups, setLocalGroups] = useState(groups);
  const inputRef = useRef(null);

  // Sync if groups prop changes
  useEffect(() => {
    setLocalGroups(groups);
  }, [groups]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Find preset groups for subset rule
  const friendsGroup = localGroups.find((g) => g.name === 'Friends' && g.is_preset);
  const closeFriendsGroup = localGroups.find((g) => g.name === 'Close Friends' && g.is_preset);

  // Toggle a group with Close Friends subset enforcement
  const toggleGroup = (groupId) => {
    const next = new Set(localSelected);

    if (next.has(groupId)) {
      // Unchecking
      next.delete(groupId);

      // Unchecking Friends -> also uncheck Close Friends
      if (friendsGroup && closeFriendsGroup && groupId === friendsGroup.id) {
        next.delete(closeFriendsGroup.id);
      }
    } else {
      // Checking
      next.add(groupId);

      // Checking Close Friends -> also check Friends
      if (friendsGroup && closeFriendsGroup && groupId === closeFriendsGroup.id) {
        next.add(friendsGroup.id);
      }
    }

    setLocalSelected(next);
  };

  // Add a custom group
  const handleAddCustomGroup = async () => {
    const trimmed = customGroupInput.trim();
    if (!trimmed) return;

    // Check if name already exists (case-insensitive)
    const existing = localGroups.find((g) => g.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      // Select it if not already selected
      const next = new Set(localSelected);
      next.add(existing.id);
      setLocalSelected(next);
      setCustomGroupInput('');
      return;
    }

    const newGroup = await onCreateGroup(trimmed);
    if (newGroup) {
      setLocalGroups((prev) => [...prev, newGroup]);
      const next = new Set(localSelected);
      next.add(newGroup.id);
      setLocalSelected(next);
    }
    setCustomGroupInput('');
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomGroup();
    }
  };

  // Delete a custom group
  const handleDeleteGroup = async (groupId, e) => {
    e.stopPropagation();
    const success = await onDeleteGroup(groupId);
    if (success) {
      setLocalGroups((prev) => prev.filter((g) => g.id !== groupId));
      const next = new Set(localSelected);
      next.delete(groupId);
      setLocalSelected(next);
    }
  };

  // Done: save and close. Also adds pending input text.
  const handleDone = async () => {
    let finalSelected = new Set(localSelected);

    // If there's text in the input, add it as a custom group
    const trimmed = customGroupInput.trim();
    if (trimmed) {
      const existing = localGroups.find((g) => g.name.toLowerCase() === trimmed.toLowerCase());
      if (existing) {
        finalSelected.add(existing.id);
      } else {
        const newGroup = await onCreateGroup(trimmed);
        if (newGroup) {
          finalSelected.add(newGroup.id);
        }
      }
    }

    onSave([...finalSelected]);
    onClose();
  };

  // Split into presets and custom for display
  const presetGroups = localGroups.filter((g) => g.is_preset);
  const customGroups = localGroups.filter((g) => !g.is_preset);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      {/* Bottom sheet */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          boxShadow: SHADOWS.TIER3,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
        }}
      >
        {/* Drag handle */}
        <div style={{ padding: '12px 0 8px', display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: '36px',
              height: '4px',
              backgroundColor: '#D1D1D6',
              borderRadius: '2px',
            }}
          />
        </div>

        {/* Group options - scrollable */}
        <div
          style={{
            padding: '0 16px',
            maxHeight: '40vh',
            overflowY: 'auto',
          }}
        >
          {/* Preset groups */}
          {presetGroups.map((group) => (
            <GroupOption
              key={group.id}
              label={group.name}
              isSelected={localSelected.has(group.id)}
              onClick={() => toggleGroup(group.id)}
            />
          ))}

          {/* Custom groups */}
          {customGroups.map((group) => (
            <GroupOption
              key={group.id}
              label={group.name}
              isSelected={localSelected.has(group.id)}
              isCustom
              onClick={() => toggleGroup(group.id)}
              onDelete={(e) => handleDeleteGroup(group.id, e)}
            />
          ))}
        </div>

        {/* Add group input */}
        <div style={{ padding: '0 16px' }}>
          <div style={{ marginTop: '8px', marginBottom: '16px' }}>
            <PillInput
              ref={inputRef}
              variant="secondary"
              value={customGroupInput}
              onChange={(e) => setCustomGroupInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Add group"
            />
          </div>
        </div>

        {/* Done button */}
        <div style={{ padding: '0 16px' }}>
          <ButtonRounded text="Done" onClick={handleDone} size="full" variant="primary" />
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * GroupOption - Individual group option row
 */
const GroupOption = ({ label, isSelected, isCustom, onClick, onDelete }) => (
  <div
    onClick={onClick}
    style={{
      position: 'relative',
      padding: '14px 16px',
      paddingRight: isCustom ? '60px' : '44px',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      backgroundColor: isSelected ? '#F0F0F0' : 'transparent',
      borderRadius: '24px',
      marginBottom: '4px',
      transition: 'background-color 0.15s ease',
    }}
  >
    <span
      style={{
        fontSize: '16px',
        color: '#333',
        fontWeight: isSelected ? '500' : '400',
      }}
    >
      {label}
    </span>

    {/* Checkmark */}
    {isSelected && (
      <Check
        size={18}
        color="#333"
        weight="bold"
        style={{
          position: 'absolute',
          right: isCustom ? '40px' : '16px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      />
    )}

    {/* Delete button for custom groups */}
    {isCustom && onDelete && (
      <X
        size={16}
        color="#999"
        weight="bold"
        onClick={onDelete}
        style={{
          position: 'absolute',
          right: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
        }}
        aria-label="Delete custom group"
      />
    )}
  </div>
);

export default GroupSelector;
